/**
 * Dynamic Content Indexing System
 * Automatically extracts and indexes content from the entire portfolio
 * Works production-ready with any new content added
 */

export interface SearchableItem {
  id: string;
  type: "section" | "project" | "skill" | "experience";
  title: string;
  content: string;
  keywords: string[];
  anchor: string;
  displayText: string;
}

/**
 * Extract all searchable content from the DOM dynamically
 * This scans the page structure and indexes everything
 */
export function buildSearchIndex(): SearchableItem[] {
  const items: SearchableItem[] = [];

  // === 1. Index all sections from all pages ===
  const allSections = document.querySelectorAll('[data-section-id]');
  allSections.forEach((section) => {
    const id = section.getAttribute('data-section-id') || '';
    const page = section.getAttribute('data-page') || 'home';
    const text = (section as HTMLElement).innerText || '';
    const title = text.split('\n')[0] || id;
    items.push({
      id: `section-${page}-${id}`,
      type: 'section',
      title: `${page.charAt(0).toUpperCase() + page.slice(1)}: ${id.charAt(0).toUpperCase() + id.slice(1)}`,
      content: text,
      keywords: generateKeywords(text),
      anchor: `#${id}`,
      displayText: `${page.charAt(0).toUpperCase() + page.slice(1)}: ${id.charAt(0).toUpperCase() + id.slice(1)}`,
    });
  });

  // === 2. Extract project data dynamically ===
  // Look for project cards in the DOM
  const projectCards = document.querySelectorAll("[data-project]");
  projectCards.forEach((card, idx) => {
    const title = card.getAttribute("data-project-title") || `Project ${idx + 1}`;
    const description = card.getAttribute("data-project-desc") || "";
    const tags = card.getAttribute("data-project-tags") || "";

    items.push({
      id: `project-${idx}`,
      type: "project",
      title,
      content: description,
      keywords: generateKeywords(`${title} ${description} ${tags}`),
      anchor: "#projects",
      displayText: title,
    });
  });

  // === 3. Extract skill data dynamically ===
  const skillsSection = document.getElementById("skills");
  if (skillsSection) {
    const skillElements = skillsSection.querySelectorAll("[data-skill]");
    skillElements.forEach((skill, idx) => {
      const skillName = skill.textContent || `Skill ${idx + 1}`;

      items.push({
        id: `skill-${idx}`,
        type: "skill",
        title: skillName,
        content: skillName,
        keywords: generateKeywords(skillName),
        anchor: "#skills",
        displayText: skillName,
      });
    });
  }

  // === 4. Extract experience data dynamically ===
  const experienceSection = document.getElementById("experience");
  if (experienceSection) {
    const expElements = experienceSection.querySelectorAll("[data-experience]");
    expElements.forEach((exp, idx) => {
      const expTitle = exp.getAttribute("data-exp-title") || `Experience ${idx + 1}`;
      const expDesc = exp.getAttribute("data-exp-desc") || "";

      items.push({
        id: `exp-${idx}`,
        type: "experience",
        title: expTitle,
        content: expDesc,
        keywords: generateKeywords(`${expTitle} ${expDesc}`),
        anchor: "#experience",
        displayText: expTitle,
      });
    });
  }

  return items;
}

/**
 * Generate searchable keywords from text
 * Extracts meaningful words for fuzzy matching
 */
function generateKeywords(text: string): string[] {
  if (!text) return [];

  // Split into words and filter
  const keywords = text
    .toLowerCase()
    .split(/\s+|[,\/\-()[\]{}]/)
    .filter((word) => word.length > 2 && !isStopWord(word))
    .slice(0, 30); // Limit to 30 keywords

  return Array.from(new Set(keywords)); // Remove duplicates
}

/**
 * Common stop words to filter out
 */
function isStopWord(word: string): boolean {
  const stopWords = new Set([
    "the",
    "and",
    "for",
    "with",
    "that",
    "this",
    "from",
    "are",
    "was",
    "been",
    "have",
    "has",
    "not",
    "can",
    "you",
    "or",
    "an",
    "as",
    "by",
    "at",
    "to",
    "in",
    "of",
    "on",
    "is",
  ]);
  return stopWords.has(word);
}

/**
 * Fuzzy search through indexed content
 * Returns items matching the query
 */
export function searchContent(query: string, items: SearchableItem[]): SearchableItem[] {
  if (!query.trim()) return [];

  const q = query.toLowerCase();

  return items.filter((item) => {
    // Check if any keyword matches
    const titleMatch = item.title.toLowerCase().includes(q);
    const contentMatch = item.content.toLowerCase().includes(q);
    const keywordMatch = item.keywords.some((keyword) => keyword.includes(q) || q.includes(keyword));

    return titleMatch || contentMatch || keywordMatch;
  });
}
